import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import {
  useSemmy,
} from '@/hooks/useSemmy';
import type { SemmyAction, SemmyDeferred, SemmyAuthority, ActionType, SemmyPerformance } from '@/hooks/useSemmy';
import { ClientDrawer } from '@/components/ClientDrawer';
import { SemmyAvatar } from '@/components/SemmyAvatar';
import {
  Clock, CheckCircle2, XCircle, Edit3, Send,
  ChevronDown, Zap, Shield, AlertTriangle, PhoneCall,
  TrendingUp, Activity,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

// ── Visual config per action type ──────────────────────────────────────────────

const TYPE_LABEL: Record<ActionType, string> = {
  first_contact:        'Eerste contact',
  follow_up:            'Opvolging',
  quote_communication:  'Offerte',
  scheduling:           'Inplannen',
  complaint_response:   'Klacht',
  relationship_close:   'Afsluiten',
  general_communication: 'Algemeen',
};

const CLIENT_STATUS_LABEL: Record<string, string> = {
  new:      'nieuw',
  active:   'actief',
  at_risk:  'risicoklant',
  inactive: 'inactief',
};

const TYPE_COLOR: Record<ActionType, { bg: string; text: string; border: string }> = {
  first_contact:        { bg: 'var(--badge-first-contact-bg)',  text: 'var(--badge-first-contact-text)',  border: 'var(--badge-first-contact-border)' },
  follow_up:            { bg: 'var(--badge-follow-up-bg)',      text: 'var(--badge-follow-up-text)',      border: 'var(--badge-follow-up-border)' },
  quote_communication:  { bg: 'var(--badge-quote-bg)',          text: 'var(--badge-quote-text)',          border: 'var(--badge-quote-border)' },
  scheduling:           { bg: 'var(--badge-scheduling-bg)',     text: 'var(--badge-scheduling-text)',     border: 'var(--badge-scheduling-border)' },
  complaint_response:   { bg: 'var(--danger-bg)',  text: 'var(--danger-text)',  border: 'var(--danger-border)' },
  relationship_close:   { bg: 'var(--warning-bg)', text: 'var(--warning-text)', border: 'var(--warning-border)' },
  general_communication:{ bg: 'var(--bg-input)',   text: 'var(--text-tertiary)', border: 'var(--border-primary)' },
};

// action types that always require approval regardless of stakes or authority
const ALWAYS_REVIEWED: ActionType[] = ['first_contact', 'complaint_response'];

// ── Helpers ────────────────────────────────────────────────────────────────────

function StakesBadge({ score }: { score: number }) {
  const color = score >= 7 ? 'var(--danger-text)' : score >= 4 ? 'var(--warning-text)' : 'var(--text-muted)';
  const bg    = score >= 7 ? 'var(--danger-bg)'   : score >= 4 ? 'var(--warning-bg)'   : 'var(--bg-input)';
  return (
    <span className="text-[11px] font-semibold px-1.5 py-0.5 border"
      style={{ color, background: bg, borderColor: color, borderRadius: '4px' }}>
      Risico {score}/10
    </span>
  );
}

function SentimentDot({ score }: { score: number }) {
  const color = score >= 0.7 ? 'var(--success-dot)' : score >= 0.4 ? 'var(--warning-dot)' : 'var(--danger-dot)';
  const label = score >= 0.7 ? 'positief' : score >= 0.4 ? 'neutraal' : 'negatief';
  return (
    <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}
      title={`Sentiment: ${label} (${score.toFixed(2)})`}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function TypeBadge({ type }: { type: ActionType }) {
  const c = TYPE_COLOR[type];
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 border"
      style={{ background: c.bg, color: c.text, borderColor: c.border, borderRadius: '4px' }}>
      {TYPE_LABEL[type]}
    </span>
  );
}

function ClientStatusDot({ status }: { status: SemmyAction['client_status'] }) {
  const colors: Record<string, string> = {
    new: 'var(--accent)',
    active: 'var(--success-dot)',
    at_risk: 'var(--danger-dot)',
    inactive: 'var(--text-muted)',
  };
  return (
    <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: colors[status] }} />
      {CLIENT_STATUS_LABEL[status] ?? status.replace('_', ' ')}
    </span>
  );
}

function ago(dateStr: string) {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: nl });
  } catch {
    return '—';
  }
}

function duration(dateStr: string) {
  try {
    return formatDistanceToNow(new Date(dateStr), { locale: nl });
  } catch {
    return '—';
  }
}

function parseLeadContext(inboundContent: string | null | undefined): { vloer?: string; m2?: string; plaats?: string } {
  if (!inboundContent) return {};
  const vloer  = inboundContent.match(/Interesse:\s*([^\n]+)/)?.[1]?.trim();
  const m2raw  = inboundContent.match(/Oppervlakte:\s*([0-9.,]+)/)?.[1]?.trim();
  const plaats = inboundContent.match(/Plaats:\s*([^\n]+)/)?.[1]?.trim();
  return { vloer, m2: m2raw, plaats };
}

// ── Manifest panel (compact details link) ─────────────────────────────────────

function ManifestPanel({ action }: { action: SemmyAction }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="hover-muted text-[11px]"
        style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
      >
        {open ? 'verberg details' : 'details'}
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border p-2.5 space-y-1.5" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {[
              ['Type actie',          TYPE_LABEL[action.action_type]],
              ['Clientstatus',        CLIENT_STATUS_LABEL[action.client_status] ?? action.client_status.replace('_', ' ')],
              ['Sentimentscore',      action.sentiment_score.toFixed(2)],
              ['Risicoscore',         `${action.stakes_score}/10`],
              ['Vereist goedkeuring', action.requires_approval ? 'Ja' : 'Nee'],
              ['Autonoom versturen',  action.autonomous_send  ? 'Ja' : 'Nee'],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{k}</p>
                <p className="text-[11px] font-medium" style={{ color: 'var(--text-secondary)' }}>{v}</p>
              </div>
            ))}
          </div>
          {action.approval_reason && (
            <div className="pt-1 border-t" style={{ borderColor: 'var(--border-separator)' }}>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Reden goedkeuring</p>
              <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{action.approval_reason}</p>
            </div>
          )}
          <div className="pt-1 border-t" style={{ borderColor: 'var(--border-separator)' }}>
            <p className="text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Risico-invoer</p>
            <div className="flex flex-wrap gap-1">
              {(Object.entries(action.stakes_inputs) as [string, boolean][]).map(([k, v]) => (
                <span key={k} className="text-[9px] px-1.5 py-0.5 rounded border"
                  style={{
                    background: v ? 'var(--warning-bg)' : 'transparent',
                    color: v ? 'var(--warning-text)' : 'var(--text-muted)',
                    borderColor: v ? 'var(--warning-border)' : 'var(--border-separator)',
                  }}>
                  {k.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Thread preview ─────────────────────────────────────────────────────────────

interface ThreadMessage {
  role: string;
  content: string;
  created_at: string;
}

function ThreadPreview({ messages, loading }: { messages: ThreadMessage[]; loading: boolean }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div style={{ marginBottom: '12px' }}>
        {[0, 1].map(i => (
          <div key={i} style={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end', marginBottom: '6px' }}>
            <div className="skeleton" style={{ height: '28px', width: i % 2 === 0 ? '65%' : '50%', borderRadius: '6px' }} />
          </div>
        ))}
        <div style={{ borderBottom: '1px solid var(--border-separator)', margin: '10px 0', opacity: 0.4 }} />
      </div>
    );
  }

  if (messages.length === 0) return null;

  const visible = expanded ? messages : messages.slice(-2);
  const hiddenCount = Math.max(0, messages.length - 2);

  return (
    <div style={{ marginBottom: '12px' }}>
      {hiddenCount > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          style={{
            fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px',
            display: 'block', cursor: 'pointer', background: 'none', border: 'none',
            padding: 0, textDecoration: 'underline', textDecorationStyle: 'dotted',
            textUnderlineOffset: '2px',
          }}
        >
          {hiddenCount} eerder{hiddenCount !== 1 ? 'e' : ''} bericht{hiddenCount !== 1 ? 'en' : ''} tonen
        </button>
      )}

      {visible.map((m, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: m.role === 'client' ? 'flex-start' : 'flex-end', marginBottom: '5px' }}>
          <div style={{
            maxWidth: '82%', padding: '6px 10px', fontSize: '12px', lineHeight: '1.45',
            color: 'var(--text-secondary)',
            background: m.role === 'client' ? 'var(--bg-input)' : 'var(--accent-dim)',
            border: `1px solid ${m.role === 'client' ? 'var(--border-primary)' : 'rgba(113,180,112,0.20)'}`,
            borderRadius: m.role === 'client' ? '3px 10px 10px 10px' : '10px 3px 10px 10px',
          }}>
            {m.content.length > 140 ? m.content.slice(0, 140) + '…' : m.content}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0' }}>
        <div style={{ flex: 1, height: '1px', background: 'var(--border-separator)' }} />
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.6 }}>Semmy's concept</span>
        <div style={{ flex: 1, height: '1px', background: 'var(--border-separator)' }} />
      </div>
    </div>
  );
}

// ── Queued card ────────────────────────────────────────────────────────────────

interface QueuedCardProps {
  action: SemmyAction;
  onApprove: () => void;
  onApproveWithEdit: (text: string, category: string, note?: string) => void;
  onReject: (reason?: string) => void;
  onOpenDrawer: () => void;
  loadClientThread: (clientId: string | null) => Promise<ThreadMessage[]>;
  semmyAvatarUrl: string | null;
  isExiting?: boolean;
  isLatest?:  boolean;
}

const CORRECTION_CATEGORIES = [
  { value: 'tone',                label: 'Toon' },
  { value: 'pricing',             label: 'Prijs' },
  { value: 'commitment_language', label: 'Toezegging' },
  { value: 'relationship_read',   label: 'Klantinschatting' },
  { value: 'factual',             label: 'Feitelijk' },
  { value: 'other',               label: 'Anders' },
];

function QueuedCard({ action, onApprove, onApproveWithEdit, onReject, onOpenDrawer, loadClientThread, semmyAvatarUrl, isExiting, isLatest }: QueuedCardProps) {
  const [mode, setMode] = useState<'view' | 'edit' | 'reject'>('view');
  const [approving, setApproving] = useState(false);
  const [editText, setEditText]           = useState(action.draft_content);
  const [editCategory, setEditCategory]   = useState('tone');
  const [correctionNote, setCorrectionNote] = useState('');
  const [rejectReason, setRejectReason]   = useState('');
  const [thread, setThread]               = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(true);
  const [showDetails, setShowDetails]     = useState(false);

  useEffect(() => {
    setThreadLoading(true);
    loadClientThread(action.client_id).then(messages => {
      setThread(messages);
      setThreadLoading(false);
    });
  }, [action.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = parseLeadContext(action.inbound_content);
  const fbTimestamp = action.facebook_created_at || action.created_at;

  return (
    <div className={`action-card-wrap${isExiting ? ' card-exiting' : ''}${isLatest ? ' is-latest' : ''}`}>
    <div className="action-card-inner"
      style={{ background: 'var(--bg-surface-raised)' }}>
      {/* Card header */}
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <TypeBadge type={action.action_type} />
            {action.source === 'phone_call' && (
              <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border"
                style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-separator)' }}>
                <PhoneCall size={10} />
                Telefoon
              </span>
            )}
          </div>
          <button
            onClick={onOpenDrawer}
            className="text-[14px] font-semibold text-left hover:underline"
            style={{ color: 'var(--text-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            {action.client_name}
          </button>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <ClientStatusDot status={action.client_status} />
            <SentimentDot score={action.sentiment_score} />
            <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              <Clock size={9} />
              Aangemeld {duration(fbTimestamp)}
            </span>
          </div>
          {(ctx.vloer || ctx.m2 || ctx.plaats) && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {ctx.vloer && (
                <span className="text-[11px] px-2 py-0.5 rounded-md"
                  style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                  {ctx.vloer}
                </span>
              )}
              {ctx.m2 && <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{ctx.m2} m²</span>}
              {ctx.plaats && <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{ctx.plaats}</span>}
            </div>
          )}
        </div>
        <div className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)' }}>
          <Shield size={10} style={{ color: 'var(--warning-text)' }} />
        </div>
      </div>

      {/* Draft content or edit textarea */}
      <div className="px-4 pb-3">
        {mode === 'view' && (
          <>
            <ThreadPreview messages={thread} loading={threadLoading} />
            <div className="flex items-start gap-2">
              <SemmyAvatar size={20} imageUrl={semmyAvatarUrl} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--accent)' }}>Semmy</div>
                <div className="rounded-lg p-3 text-[13px] leading-relaxed whitespace-pre-wrap semmy-draft-bubble"
                  style={{
                    background: 'var(--bg-input)', color: 'var(--text-secondary)',
                    border: '1px solid var(--border-separator)',
                    borderLeft: '2px solid var(--accent)',
                    borderRadius: '4px 12px 12px 12px',
                  }}>
                  {action.draft_content}
                </div>
              </div>
            </div>
          </>
        )}

        {mode === 'edit' && (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              rows={6}
              className="w-full rounded-lg p-3 text-[13px] leading-relaxed resize-none outline-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-hover)' }}
            />
            <div className="flex items-center gap-2">
              <label className="text-[11px] shrink-0" style={{ color: 'var(--text-muted)' }}>Correctietype:</label>
              <select
                value={editCategory}
                onChange={e => setEditCategory(e.target.value)}
                className="text-[11px] px-2 py-1 rounded-md outline-none"
                style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
                {CORRECTION_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--text-primary)' }}>
                Wat was er mis?
              </label>
              <textarea
                value={correctionNote}
                onChange={e => setCorrectionNote(e.target.value)}
                placeholder="Bijv. te formeel, prijs klopt niet, verkeerde aanname over klant..."
                rows={4}
                className="correction-note w-full rounded-lg p-2.5 text-[12px] leading-relaxed resize-none outline-none"
                style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-separator)' }}
              />
            </div>
          </div>
        )}

        {mode === 'reject' && (
          <div className="space-y-2">
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              Optioneel: vertel Semmy waarom — dit helpt haar verbeteren.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Bijv. Verkeerde toon voor deze klant, timing klopt niet..."
              rows={3}
              className="w-full rounded-lg p-3 text-[13px] leading-relaxed resize-none outline-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--danger-border)' }}
            />
          </div>
        )}
      </div>

      {/* Collapsible details */}
      {mode === 'view' && (
        <div className="px-4 pb-2">
          <button
            onClick={() => setShowDetails(o => !o)}
            className="text-[11px]"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', textDecoration: 'underline', textDecorationStyle: 'dotted', padding: 0 }}
          >
            {showDetails ? 'verberg details' : 'details'}
          </button>
          {showDetails && (
            <div className="mt-1.5 rounded-lg border p-2.5 space-y-1.5" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {[
                  ['Type actie',          TYPE_LABEL[action.action_type]],
                  ['Clientstatus',        CLIENT_STATUS_LABEL[action.client_status] ?? action.client_status.replace('_', ' ')],
                  ['Sentimentscore',      action.sentiment_score.toFixed(2)],
                  ['Risicoscore',         `${action.stakes_score}/10`],
                  ['Vereist goedkeuring', action.requires_approval ? 'Ja' : 'Nee'],
                  ['Autonoom versturen',  action.autonomous_send  ? 'Ja' : 'Nee'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{k}</p>
                    <p className="text-[11px] font-medium" style={{ color: 'var(--text-secondary)' }}>{v}</p>
                  </div>
                ))}
              </div>
              {action.approval_reason && (
                <div className="pt-1 border-t" style={{ borderColor: 'var(--border-separator)' }}>
                  <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Reden goedkeuring</p>
                  <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{action.approval_reason}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="px-4 pb-4">
        {mode === 'view' && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={async () => {
                if (approving) return;
                setApproving(true);
                await new Promise(r => setTimeout(r, 400));
                onApprove();
              }}
              disabled={approving}
              onPointerDown={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.97)'; }}
              onPointerUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
              onPointerLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
              className="btn btn-primary btn-block semmy-versturen-btn"
              style={{
                background: approving ? 'var(--bg-input)' : undefined,
                color: approving ? 'var(--text-secondary)' : undefined,
              }}
            >
              {approving ? (
                <div style={{
                  width: '11px', height: '11px',
                  border: '1.5px solid var(--border-primary)',
                  borderTopColor: 'var(--text-secondary)',
                  borderRadius: '50%',
                  animation: 'spin 0.7s linear infinite',
                }} />
              ) : (
                <>
                  <Send size={13} />
                  Versturen
                </>
              )}
            </button>
            <button
              aria-label="Concept bewerken"
              onClick={() => { setEditText(action.draft_content); setMode('edit'); }}
              className="btn-icon">
              <Edit3 size={15} />
            </button>
            <button
              aria-label="Concept afwijzen"
              onClick={() => setMode('reject')}
              className="btn-icon">
              <XCircle size={15} />
            </button>
          </div>
        )}

        {mode === 'edit' && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { onApproveWithEdit(editText, editCategory, correctionNote || undefined); }}
              disabled={editText.trim() === ''}
              className="btn btn-primary btn-sm">
              <Send size={11} />
              Opslaan & Versturen
            </button>
            <button
              onClick={() => setMode('view')}
              className="btn btn-ghost btn-sm">
              Annuleren
            </button>
          </div>
        )}

        {mode === 'reject' && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { onReject(rejectReason || undefined); }}
              className="btn btn-destructive-confirm btn-sm">
              <XCircle size={11} />
              Bevestig afwijzing
            </button>
            <button
              onClick={() => setMode('view')}
              className="btn btn-ghost btn-sm">
              Annuleren
            </button>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

// ── Completed item ─────────────────────────────────────────────────────────────

function CompletedItem({ action, hadCorrection, onOpenDrawer }: { action: SemmyAction; hadCorrection: boolean; onOpenDrawer: () => void }) {
  const [open, setOpen] = useState(false);
  const isRejected = action.status === 'rejected';
  const sentContent = action.approved_content || action.draft_content;

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ background: isRejected ? 'var(--bg-input)' : 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)', opacity: isRejected ? 0.8 : 1 }}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
        className="w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer">
        <div className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{
            background: isRejected ? 'var(--danger-bg)' : action.autonomous_send ? 'var(--accent-dim)' : 'var(--bg-input)',
            border: `1px solid ${isRejected ? 'var(--danger-border)' : action.autonomous_send ? 'var(--accent)' : 'var(--border-primary)'}`,
          }}>
          {isRejected
            ? <XCircle size={10} style={{ color: 'var(--danger-text)' }} />
            : action.autonomous_send
              ? <Zap size={10} style={{ color: 'var(--accent)' }} />
              : <CheckCircle2 size={10} style={{ color: 'var(--text-muted)' }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={e => { e.stopPropagation(); onOpenDrawer(); }}
              className="text-[13px] font-medium text-left hover:underline"
              style={{ color: 'var(--text-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              {action.client_name}
            </button>
            <TypeBadge type={action.action_type} />
            {action.source === 'phone_call' && (
              <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border"
                style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-separator)' }}>
                <PhoneCall size={10} />
                Telefoon
              </span>
            )}
            {hadCorrection && (
              <span className="text-[9px] px-1.5 py-0.5 rounded border"
                style={{ background: 'var(--badge-quote-bg)', color: 'var(--badge-quote-text)', borderColor: 'var(--badge-quote-border)' }}>
                bewerkt door operator
              </span>
            )}
            {action.autonomous_send && (
              <span className="text-[9px] px-1.5 py-0.5 rounded border"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' }}>
                autonoom
              </span>
            )}
            {isRejected && (
              <span className="text-[9px] px-1.5 py-0.5 rounded border"
                style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}>
                afgewezen
              </span>
            )}
          </div>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{ago(action.created_at)}</p>
        </div>
        <ChevronDown size={12} className={`shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-muted)' }} />
      </div>
      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border-separator)' }}>

          {/* "Not sent" banner for rejected items */}
          {isRejected && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '3px 8px', borderRadius: '4px', margin: '12px 0 8px',
              background: 'var(--danger-bg)', border: '1px solid var(--danger-border)',
              fontSize: '11px', fontWeight: '600', color: 'var(--danger-text)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>
              ✕ Niet verstuurd
            </div>
          )}

          {/* Client message — what triggered this action */}
          {(action.inbound_content || action.draft_content) && !isRejected && (
            <div style={{ paddingTop: '12px', marginBottom: '8px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-muted)',
                marginBottom: '6px',
              }}>Klant</div>
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '8px 12px',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '3px 10px 10px 10px',
                  whiteSpace: 'pre-wrap',
                }}>
                  {action.inbound_content ?? '(bericht niet beschikbaar)'}
                </div>
              </div>
            </div>
          )}

          {/* Semmy's reply — sent or rejected draft */}
          {isRejected ? (
            <div>
              <div style={{
                fontSize: '11px', fontWeight: '500', textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '6px',
              }}>
                Concept (niet verstuurd)
              </div>
              <p style={{
                fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5',
                textDecoration: 'line-through', fontStyle: 'italic',
              }}>
                {action.draft_content}
              </p>
            </div>
          ) : (
            <div>
              <div style={{
                fontSize: '11px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--accent)',
                marginBottom: '6px',
              }}>Semmy</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '8px 12px',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: 'var(--text-secondary)',
                  background: 'var(--accent-dim)',
                  border: '1px solid var(--accent)',
                  borderRadius: '10px 3px 10px 10px',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '120px',
                  overflowY: 'auto',
                }}>
                  {action.approved_content || action.draft_content}
                </div>
              </div>
            </div>
          )}

          {/* Rejection reason if rejected */}
          {action.rejection_reason && (
            <p style={{ marginTop: '8px', fontSize: '11px', color: 'var(--danger-text)' }}>
              Reden afwijzing: {action.rejection_reason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Deferred card ──────────────────────────────────────────────────────────────

function DeferredCard({ item, onDismiss, onOpenLead }: {
  item: SemmyDeferred;
  onDismiss: () => void;
  onOpenLead: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reminded, setReminded] = useState(false);

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
      <button onClick={() => setOpen(o => !o)} className="w-full text-left px-4 py-3 flex items-start gap-3">
        <div className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)' }}>
          <Clock size={10} style={{ color: 'var(--text-muted)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>{item.client_name}</p>
          <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
            {item.considered_action}
          </p>
        </div>
        <ChevronDown size={12} className={`shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--border-separator)' }}>
          <div className="pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
              Waarom Semmy niet heeft gehandeld
            </p>
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {item.deferral_reason}
            </p>
          </div>
          <div className="p-3 rounded-lg border" style={{ background: 'var(--accent-dim)', borderColor: 'var(--accent)' }}>
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--accent)' }}>
              Semmy raadt aan
            </p>
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {item.recommended_next_action}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <Clock size={10} style={{ color: 'var(--accent)' }} />
              <p className="text-[11px] font-medium" style={{ color: 'var(--accent)' }}>
                {item.recommended_trigger}
              </p>
            </div>
          </div>
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            Overwogen {ago(item.created_at)}
          </p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', paddingTop: '4px' }}>
            <button
              onClick={onDismiss}
              style={{
                fontSize: '11px', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                background: 'var(--bg-input)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-primary)',
              }}>
              Zelf afhandelen
            </button>
            <button
              onClick={() => setReminded(true)}
              disabled={reminded}
              style={{
                fontSize: '11px', padding: '4px 10px', borderRadius: '6px', cursor: reminded ? 'default' : 'pointer',
                background: reminded ? 'var(--accent-dim)' : 'var(--bg-input)',
                color: reminded ? 'var(--accent)' : 'var(--text-secondary)',
                border: `1px solid ${reminded ? 'var(--accent)' : 'var(--border-primary)'}`,
              }}>
              {reminded ? 'Herinnering ingesteld ✓' : 'Herinnering morgen'}
            </button>
            <button
              onClick={onOpenLead}
              style={{
                fontSize: '11px', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                background: 'transparent', color: 'var(--accent)',
                border: '1px solid var(--accent)',
              }}>
              Open lead →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Trust progress panel ───────────────────────────────────────────────────────

function TrustProgressPanel({
  authority,
  onGrantAutonomy,
}: {
  authority: SemmyAuthority[];
  onGrantAutonomy: (type: ActionType) => void;
}) {
  return (
    <div className="rounded-xl border p-4"
      style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={13} style={{ color: 'var(--text-muted)' }} />
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Vertrouwensvoortgang · Semmy's verdiende autonomie
          </span>
          <span style={{
            fontSize: '11px',
            color: 'var(--text-muted)',
            fontWeight: '400',
            textTransform: 'none',
            letterSpacing: 'normal',
            display: 'block',
            marginTop: '2px',
          }}>
            Als Semmy voldoende acties correct afhandelt, stelt ze autonomie voor. Jij beslist altijd.
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {authority.map(auth => {
          const isAlwaysReviewed = ALWAYS_REVIEWED.includes(auth.action_type as ActionType);
          const isAutonomous     = auth.authority_status === 'autonomous';
          const isProposal       = auth.authority_status === 'proposal_queued';
          const pct = Math.min(100, (auth.consecutive_clean_approvals / auth.authority_proposal_threshold) * 100);
          const c = TYPE_COLOR[auth.action_type as ActionType];

          return (
            <div key={auth.action_type} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-1">
                <span className="text-[11px] font-medium" style={{ color: c.text }}>
                  {TYPE_LABEL[auth.action_type as ActionType]}
                </span>
                {isAutonomous && (
                  <span title="Autonoom"><Zap size={10} style={{ color: 'var(--accent)' }} /></span>
                )}
                {isAlwaysReviewed && (
                  <span title="Altijd beoordeeld"><Shield size={10} style={{ color: 'var(--text-muted)' }} /></span>
                )}
              </div>

              {isAlwaysReviewed ? (
                <div className="text-[9px] leading-tight" style={{ color: 'var(--text-muted)' }}>
                  Altijd beoordeeld
                </div>
              ) : isAutonomous ? (
                <div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div className="h-full rounded-full" style={{ width: '100%', background: 'var(--accent)' }} />
                  </div>
                  <p className="text-[9px] mt-1" style={{ color: 'var(--accent)' }}>Autonoom ✓</p>
                </div>
              ) : isProposal ? (
                <div className="space-y-1">
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div className="h-full rounded-full" style={{ width: '100%', background: 'var(--warning-text)' }} />
                  </div>
                  <button
                    onClick={() => onGrantAutonomy(auth.action_type as ActionType)}
                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded border w-full text-center"
                    style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)', borderColor: 'var(--warning-border)' }}>
                    Bekijk voorstel →
                  </button>
                </div>
              ) : (
                <div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: c.text }} />
                  </div>
                  <p className="text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    {auth.consecutive_clean_approvals}/{auth.authority_proposal_threshold}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Autonomy proposal banner ───────────────────────────────────────────────────

function ProposalBanner({
  authority,
  onGrant,
}: {
  authority: SemmyAuthority[];
  onGrant: (type: ActionType) => void;
}) {
  const proposals = authority.filter(a => a.authority_status === 'proposal_queued');
  if (proposals.length === 0) return null;

  return (
    <div className="rounded-xl border p-4"
      style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning-border)' }}>
      <div className="space-y-4">
        {proposals.map(p => (
          <div key={p.action_type} className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'rgba(235,155,67,0.15)', border: '1px solid var(--warning-border)' }}>
              <Zap size={14} style={{ color: 'var(--warning-text)' }} />
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-[13px] font-semibold" style={{ color: 'var(--warning-text)' }}>
                Semmy stelt autonomie voor: {TYPE_LABEL[p.action_type as ActionType]}
              </p>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Ik heb de laatste {p.consecutive_clean_approvals} {TYPE_LABEL[p.action_type as ActionType].toLowerCase()} acties behandeld met een goedkeuringspercentage van 100% en geen correcties.
                Ik adviseer dit actietype van de goedkeuringslijst te verwijderen. Op basis van het huidige volume zou dit ongeveer 2–3 uur beoordelingstijd per week besparen.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => onGrant(p.action_type as ActionType)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold"
                  style={{ background: 'var(--warning-text)', color: '#fff' }}>
                  <CheckCircle2 size={12} />
                  Autonomie verlenen
                </button>
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Je kunt dit altijd intrekken via Vertrouwensvoortgang
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Column header ──────────────────────────────────────────────────────────────

function ColumnHeader({ label, count, icon }: { label: string; count?: number; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div style={{ color: 'var(--text-muted)' }}>{icon}</div>
      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      {count !== undefined && count > 0 && (
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: count > 0 ? 'var(--accent-dim)' : 'var(--bg-input)', color: count > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
          {count}
        </span>
      )}
    </div>
  );
}

// ── Performance view ───────────────────────────────────────────────────────────

interface PerformanceViewProps {
  performance: SemmyPerformance | null;
  loading: boolean;
}

function MetricCard({ label, value, sub, accent }: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div style={{
      padding: '16px',
      background: 'var(--bg-surface-raised)',
      border: `1px solid ${accent ? 'var(--accent)' : 'var(--border-primary)'}`,
      borderRadius: '10px',
    }}>
      <div style={{
        fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '24px', fontWeight: '600',
        color: accent ? 'var(--accent)' : 'var(--text-primary)',
        letterSpacing: '-0.03em', lineHeight: 1,
        marginBottom: sub ? '4px' : 0,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{sub}</div>
      )}
    </div>
  );
}

function FunnelBar({ label, value, max, color }: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>
          {value} <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}>({pct}%)</span>
        </span>
      </div>
      <div style={{ height: '6px', background: 'var(--bg-input)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, background: color,
          borderRadius: '3px', transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
      </div>
    </div>
  );
}

function PerformanceView({ performance, loading }: PerformanceViewProps) {
  if (loading || !performance) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '10px' }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
        <MetricCard
          label="Berichten deze week"
          value={String(performance.enquiries_this_week)}
          sub="ontvangen via WhatsApp"
        />
        <MetricCard
          label="Goedgekeurd"
          value={`${performance.approval_rate}%`}
          sub={`${performance.approved} van ${performance.enquiries_this_week}`}
          accent={performance.approval_rate >= 80}
        />
        <MetricCard
          label="Autonoom verstuurd"
          value={`${performance.autonomous_rate}%`}
          sub={`${performance.autonomous} zonder review`}
        />
        <MetricCard
          label="Offertes verstuurd"
          value={String(performance.quotes_sent)}
          sub="via Semmy deze week"
        />
        <MetricCard
          label="Correcties gemaakt"
          value={String(performance.corrections_this_week)}
          sub="aanpassingen aan Semmy's drafts"
        />
        <MetricCard
          label="Trainingsdata"
          value={String(performance.corrections_this_week)}
          sub="signalen voor Semmy's leerproces"
        />
      </div>

      {/* Funnel */}
      <div style={{
        padding: '16px',
        background: 'var(--bg-surface-raised)',
        border: '1px solid var(--border-primary)',
        borderRadius: '10px',
      }}>
        <div style={{
          fontSize: '11px', color: 'var(--text-muted)', marginBottom: '14px',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          Trechter · deze week
        </div>
        <FunnelBar
          label="Ontvangen berichten"
          value={performance.enquiries_this_week}
          max={performance.enquiries_this_week}
          color="var(--text-muted)"
        />
        <FunnelBar
          label="Goedgekeurd & verstuurd"
          value={performance.approved}
          max={performance.enquiries_this_week}
          color="var(--accent)"
        />
        <FunnelBar
          label="Offertes verstuurd"
          value={performance.quotes_sent}
          max={performance.enquiries_this_week}
          color="var(--badge-quote-text, #9B8FB0)"
        />
        {performance.enquiries_this_week === 0 && (
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '8px' }}>
            Nog geen berichten deze week
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function SemmyPage() {
  const { effectivePartnerId } = useAuth();
  const navigate = useNavigate();
  const {
    queued, completed, deferred, authority, loading, sendError,
    loadActions,
    markApproved, triggerSend, requeue,
    markRejectedLocal, triggerReject,
    approveWithEdit, reject, grantAutonomy, correctionFor, loadClientThread, loadPerformance,
    semmyAvatarUrl,
  } = useSemmy(effectivePartnerId);

  useEffect(() => {
    if (!effectivePartnerId || !isSupabaseConfigured) return;
    const channel = supabase
      .channel('semmy-queue-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'semmy_actions',
        filter: `partner_id=eq.${effectivePartnerId}`,
      }, () => { loadActions(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [effectivePartnerId, loadActions]);

  const [drawer, setDrawer] = useState<{ clientId: string; clientName: string } | null>(null);
  const openDrawer = (clientId: string, clientName: string) => setDrawer({ clientId, clientName });

  // C1/C2 — undo toast + card exit animation
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const [undoQueue, setUndoQueue] = useState<Array<{
    actionId: string; clientName: string; timeoutId: ReturnType<typeof setTimeout>; kind: 'approve' | 'reject'; reason?: string;
  }>>([]);

  const handleApprove = useCallback((actionId: string, clientName: string) => {
    setExitingIds(prev => new Set(prev).add(actionId));
    setTimeout(() => {
      setExitingIds(prev => { const n = new Set(prev); n.delete(actionId); return n; });
      markApproved(actionId);
      const timeoutId = setTimeout(() => {
        triggerSend(actionId);
        setUndoQueue(prev => prev.filter(t => t.actionId !== actionId));
      }, 3000);
      setUndoQueue(prev => [...prev, { actionId, clientName, timeoutId, kind: 'approve' }]);
    }, 220);
  }, [markApproved, triggerSend]);

  const handleReject = useCallback((actionId: string, clientName: string, reason?: string) => {
    setExitingIds(prev => new Set(prev).add(actionId));
    setTimeout(() => {
      setExitingIds(prev => { const n = new Set(prev); n.delete(actionId); return n; });
      markRejectedLocal(actionId, reason);
      const timeoutId = setTimeout(() => {
        triggerReject(actionId, reason);
        setUndoQueue(prev => prev.filter(t => t.actionId !== actionId));
      }, 3000);
      setUndoQueue(prev => [...prev, { actionId, clientName, timeoutId, kind: 'reject', reason }]);
    }, 220);
  }, [markRejectedLocal, triggerReject]);

  const handleUndo = useCallback(() => {
    setUndoQueue(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      clearTimeout(last.timeoutId);
      requeue(last.actionId);
      return prev.slice(0, -1);
    });
  }, [requeue]);

  const handleUndoAll = useCallback(() => {
    setUndoQueue(prev => {
      prev.forEach(t => { clearTimeout(t.timeoutId); requeue(t.actionId); });
      return [];
    });
  }, [requeue]);

  // C4 — queue sort
  const [queueSort, setQueueSort] = useState<'newest' | 'priority'>('newest');
  const sortedQueued = useMemo(() => {
    if (queueSort === 'priority') {
      return [...queued].sort((a, b) => {
        const ac = a.action_type === 'complaint_response' ? 1 : 0;
        const bc = b.action_type === 'complaint_response' ? 1 : 0;
        if (bc !== ac) return bc - ac;
        if (b.stakes_score !== a.stakes_score) return b.stakes_score - a.stakes_score;
        return a.created_at < b.created_at ? -1 : 1;
      });
    }
    return [...queued].sort((a, b) => (b.created_at > a.created_at ? 1 : -1));
  }, [queued, queueSort]);

  // C6 — dismissed deferred items
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const visibleDeferred = useMemo(() =>
    deferred.filter(d => !dismissedIds.has(d.id)),
  [deferred, dismissedIds]);

  // Performance view
  const [view, setView] = useState<'queue' | 'results'>('results');
  const [performance, setPerformance] = useState<SemmyPerformance | null>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  // Filter controls
  const [filterType, setFilterType] = useState<ActionType | 'all'>('all');
  const [filterMode, setFilterMode] = useState<'all' | 'autonomous' | 'manual'>('all');

  const filteredCompleted = useMemo(() => {
    return completed.filter(a => {
      if (filterType !== 'all' && a.action_type !== filterType) return false;
      if (filterMode === 'autonomous' && !a.autonomous_send) return false;
      if (filterMode === 'manual' && a.autonomous_send) return false;
      return true;
    });
  }, [completed, filterType, filterMode]);

  useEffect(() => {
    if (view !== 'results') return;
    setPerfLoading(true);
    loadPerformance(weekOffset).then(p => {
      setPerformance(p);
      setPerfLoading(false);
    });
  }, [view, weekOffset, loadPerformance]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto animate-fade-up">
        <div className="flex items-start justify-between pt-1">
          <div className="space-y-2">
            <div className="skeleton h-8 w-36" />
            <div className="skeleton h-4 w-72" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {[0, 1, 2].map(i => (
            <div key={i} className="space-y-3">
              <div className="skeleton h-4 w-32" />
              <div className="skeleton h-28 rounded-xl" />
              <div className="skeleton h-28 rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const lastActionTime = [...queued, ...completed]
    .map(a => a.updated_at)
    .sort()
    .reverse()[0];

  const selectStyle = {
    fontSize: '11px',
    padding: '4px 8px',
    borderRadius: '6px',
    background: 'var(--bg-input)',
    border: '1px solid var(--border-primary)',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-up">

      {/* ── Hero identity header ── */}
      <div style={{ textAlign: 'center', paddingTop: '12px', paddingBottom: '8px' }}>

        {/* Avatar — same scale as the anna reference */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <SemmyAvatar size={96} imageUrl={semmyAvatarUrl} showOnlineDot={false} />
        </div>

        {/* Name row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
          <h1 style={{
            fontSize: 'clamp(2.8rem, 4.5vw, 4rem)',
            fontWeight: 700,
            letterSpacing: '-0.06em',
            lineHeight: 1,
            color: 'var(--text-primary)',
            textTransform: 'lowercase',
          }}>
            semmy
          </h1>
          <span style={{
            fontSize: '11px', fontWeight: 500,
            padding: '3px 10px', borderRadius: '999px',
            background: 'var(--accent-dim)', color: 'var(--accent)',
            border: '1px solid var(--accent)',
          }}>
            Actief
          </span>
        </div>

        {/* Tagline */}
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: lastActionTime ? '6px' : 0 }}>
          Een tweede gedachte. Handelt in jouw naam, met jouw oordeel.
        </p>

        {/* Last action + dev debug */}
        {lastActionTime && (
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.55, marginBottom: '2px' }}>
            Laatste actie {ago(lastActionTime)}
          </p>
        )}
        {import.meta.env.DEV && (
          <Link
            to="/partner/semmy/debug"
            className="inline-flex items-center gap-1 text-[11px] transition-opacity hover:opacity-70"
            style={{ color: 'var(--text-muted)', marginTop: '2px' }}>
            <Activity size={10} />
            Debug
          </Link>
        )}
      </div>

      {/* ── Autonomy proposals (if any) ── */}
      <ProposalBanner authority={authority} onGrant={grantAutonomy} />

      {sendError && (
        <div className="rounded-xl border px-4 py-3 flex items-start gap-3"
          style={{ background: 'var(--danger-bg)', borderColor: 'var(--danger-border)', color: 'var(--danger-text)' }}>
          <AlertTriangle size={18} />
          <div>
            <p className="font-semibold">Fout bij verzenden</p>
            <p className="text-[13px] mt-0.5" style={{ color: 'var(--danger-text)' }}>{sendError}</p>
          </div>
        </div>
      )}

      {/* ── View toggle ── */}
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        {(['queue', 'results'] as const).map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: view === v ? '500' : '400',
              background: view === v ? 'var(--accent-dim)' : 'transparent',
              color: view === v ? 'var(--accent)' : 'var(--text-muted)',
              border: `1px solid ${view === v ? 'var(--accent)' : 'var(--border-primary)'}`,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {v === 'queue' ? 'Wachtrij' : 'Resultaten'}
          </button>
        ))}
      </div>

      {/* ── Queue view ── */}
      {view === 'queue' && (
        <>
          {/* Three-column dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">

            {/* COMPLETED — desktop: left col; mobile: bottom */}
            <div className="order-3 xl:order-1">
              {/* Header + filter controls */}
              <div style={{ marginBottom: '12px' }}>
                <ColumnHeader
                  label={`Afgehandeld (${filteredCompleted.length}${filteredCompleted.length !== completed.length ? ` van ${completed.length}` : ''})`}
                  icon={<CheckCircle2 size={13} />}
                />
                {completed.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <select
                      value={filterType}
                      onChange={e => setFilterType(e.target.value as ActionType | 'all')}
                      style={{
                        ...selectStyle,
                        color: filterType !== 'all' ? 'var(--accent)' : 'var(--text-muted)',
                      }}
                    >
                      <option value="all">Alle types</option>
                      {Object.entries(TYPE_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>

                    <select
                      value={filterMode}
                      onChange={e => setFilterMode(e.target.value as 'all' | 'autonomous' | 'manual')}
                      style={{
                        ...selectStyle,
                        color: filterMode !== 'all' ? 'var(--accent)' : 'var(--text-muted)',
                      }}
                    >
                      <option value="all">Alle acties</option>
                      <option value="autonomous">Autonoom</option>
                      <option value="manual">Handmatig</option>
                    </select>

                    {(filterType !== 'all' || filterMode !== 'all') && (
                      <button
                        onClick={() => { setFilterType('all'); setFilterMode('all'); }}
                        style={{
                          ...selectStyle,
                          background: 'transparent',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Wis filters
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {completed.length === 0 && (
                  <div className="rounded-xl border p-6 text-center"
                    style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                    <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>Geen acties afgelopen 7 dagen</p>
                  </div>
                )}
                {filteredCompleted.length === 0 && completed.length > 0 && (
                  <div style={{
                    padding: '24px',
                    textAlign: 'center',
                    background: 'var(--bg-surface-raised)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '12px',
                  }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Geen resultaten voor deze filters
                    </p>
                    <button
                      onClick={() => { setFilterType('all'); setFilterMode('all'); }}
                      style={{
                        fontSize: '12px', color: 'var(--accent)',
                        background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
                      }}
                    >
                      Filters wissen
                    </button>
                  </div>
                )}
                {filteredCompleted.map(action => (
                  <CompletedItem
                    key={action.id}
                    action={action}
                    hadCorrection={correctionFor(action.id) !== null}
                    onOpenDrawer={() => openDrawer(action.client_id, action.client_name)}
                  />
                ))}
              </div>
            </div>

            {/* QUEUED — desktop: middle col; mobile: top */}
            <div className="order-1 xl:order-2">
              <div style={{ marginBottom: '12px' }}>
                <ColumnHeader
                  label="Wacht op jou"
                  count={queued.length}
                  icon={<Shield size={13} />}
                />
                {queued.length > 1 && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                    {(['newest', 'priority'] as const).map(s => (
                      <button key={s} onClick={() => setQueueSort(s)} style={{
                        fontSize: '11px', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer',
                        background: queueSort === s ? 'var(--accent-dim)' : 'transparent',
                        color: queueSort === s ? 'var(--accent)' : 'var(--text-muted)',
                        border: `1px solid ${queueSort === s ? 'var(--accent)' : 'var(--border-primary)'}`,
                      }}>
                        {s === 'newest' ? 'Nieuwste' : 'Prioriteit'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {queued.length === 0 && (
                  <div className="rounded-xl border p-6 text-center"
                    style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                    <CheckCircle2 size={16} className="mx-auto mb-2" style={{ color: 'var(--accent)' }} />
                    <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>Alles in orde — niets te beoordelen</p>
                    {completed.filter(a => a.autonomous_send && (() => {
                      const d = new Date(a.sent_at || a.approved_at || a.created_at);
                      const t = new Date();
                      return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
                    })()).length > 0 && (
                      <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                        Semmy heeft vandaag {completed.filter(a => a.autonomous_send && (() => {
                          const d = new Date(a.sent_at || a.approved_at || a.created_at);
                          const t = new Date();
                          return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
                        })()).length} berichten autonoom verstuurd
                      </p>
                    )}
                  </div>
                )}
                {sortedQueued.map((action, idx) => (
                  <QueuedCard
                    key={action.id}
                    action={action}
                    onApprove={() => handleApprove(action.id, action.client_name)}
                    onApproveWithEdit={(text, cat, note) => approveWithEdit(action.id, text, cat, note)}
                    onReject={(reason) => handleReject(action.id, action.client_name, reason)}
                    onOpenDrawer={() => openDrawer(action.client_id, action.client_name)}
                    loadClientThread={loadClientThread}
                    semmyAvatarUrl={semmyAvatarUrl}
                    isExiting={exitingIds.has(action.id)}
                    isLatest={idx === 0 && queueSort === 'newest'}
                  />
                ))}
              </div>
            </div>

            {/* DEFERRED — desktop: right col; mobile: middle */}
            <div className="order-2 xl:order-3">
              <ColumnHeader
                label="Uitgesteld · nog niet behandeld"
                count={visibleDeferred.length}
                icon={<Clock size={13} />}
              />
              <div className="space-y-2">
                {visibleDeferred.length === 0 && (
                  <div className="rounded-xl border p-6 text-center"
                    style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                        Niets uitgesteld
                      </p>
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.7, lineHeight: '1.5' }}>
                        Semmy stelt hier acties uit die ze niet kon afhandelen — te complex,
                        te gevoelig, of buiten haar bevoegdheid.
                      </p>
                    </div>
                  </div>
                )}
                {visibleDeferred.map(item => (
                  <DeferredCard
                    key={item.id}
                    item={item}
                    onDismiss={() => setDismissedIds(prev => new Set(prev).add(item.id))}
                    onOpenLead={() => navigate(`/partner/leads?client=${item.client_id}`)}
                  />
                ))}
              </div>

              {/* Correction log summary */}
              {correctionFor !== null && completed.some(a => correctionFor(a.id) !== null) && (
                <div className="mt-4">
                  <ColumnHeader label="Recente correcties" icon={<Edit3 size={13} />} />
                  <div className="rounded-xl border p-3 space-y-2"
                    style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      Elke correctie die je maakt traint Semmy's oordeel voor toekomstige berichten.
                    </p>
                    {completed
                      .filter(a => correctionFor(a.id) !== null)
                      .map(a => {
                        const corr = correctionFor(a.id)!;
                        return (
                          <div key={corr.id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-t"
                            style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                            <span>{a.client_name}</span>
                            <span className="px-1.5 py-0.5 rounded border text-[9px]"
                              style={{ background: 'var(--badge-quote-bg)', color: 'var(--badge-quote-text)', borderColor: 'var(--badge-quote-border)' }}>
                              {corr.correction_category}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* ── Trust Progress (below columns) ── */}
          <TrustProgressPanel authority={authority} onGrantAutonomy={grantAutonomy} />
        </>
      )}

      {/* ── Results view ── */}
      {view === 'results' && (
        <div>
          {/* C8 — week navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
            <button
              onClick={() => setWeekOffset(prev => prev - 1)}
              style={{
                fontSize: '12px', padding: '5px 12px', borderRadius: '7px', cursor: 'pointer',
                background: 'var(--bg-input)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-primary)',
              }}>
              ← Vorige
            </button>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '120px', textAlign: 'center' }}>
              {weekOffset === 0 ? 'Deze week' : weekOffset === -1 ? 'Vorige week' : `${-weekOffset} weken geleden`}
            </span>
            <button
              onClick={() => setWeekOffset(prev => Math.min(0, prev + 1))}
              disabled={weekOffset === 0}
              style={{
                fontSize: '12px', padding: '5px 12px', borderRadius: '7px',
                cursor: weekOffset === 0 ? 'not-allowed' : 'pointer',
                background: 'var(--bg-input)', color: weekOffset === 0 ? 'var(--text-muted)' : 'var(--text-secondary)',
                border: '1px solid var(--border-primary)',
                opacity: weekOffset === 0 ? 0.4 : 1,
              }}>
              Volgende →
            </button>
          </div>
          <PerformanceView performance={performance} loading={perfLoading} />
        </div>
      )}

      {/* ── Footer note ── */}
      <p className="text-[11px] text-center pb-4" style={{ color: 'var(--text-muted)' }}>
        Semmy handelt in jouw naam. Niets gaat naar een klant zonder jouw goedkeuring of een mandaat dat je expliciet hebt verleend.
      </p>

      {/* ── C1: Undo toast (stacked queue) ── */}
      {undoQueue.length > 0 && (() => {
        const last = undoQueue[undoQueue.length - 1];
        const count = undoQueue.length;
        const label = last.kind === 'reject'
          ? (count > 1 ? `${count} acties uitgevoerd` : `Afgewezen: ${last.clientName}`)
          : (count > 1 ? `${count} berichten verzonden` : `Verstuurd naar ${last.clientName}`);
        return (
          <div style={{
            position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
            background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)',
            borderRadius: '12px', padding: '12px 16px', minWidth: '260px', maxWidth: '360px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.24)', zIndex: 200,
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{label}</span>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {count > 1 && (
                  <button
                    onClick={handleUndoAll}
                    style={{
                      fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '6px',
                      background: 'var(--bg-input)', color: 'var(--text-secondary)',
                      border: '1px solid var(--border-primary)', cursor: 'pointer',
                    }}>
                    Alles
                  </button>
                )}
                <button
                  onClick={handleUndo}
                  style={{
                    fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '6px',
                    background: 'var(--accent-dim)', color: 'var(--accent)',
                    border: '1px solid var(--accent)', cursor: 'pointer',
                  }}>
                  Ongedaan
                </button>
              </div>
            </div>
            <div style={{ height: '3px', background: 'var(--border-separator)', borderRadius: '2px', overflow: 'hidden' }}>
              <div key={last.actionId} style={{
                height: '100%', background: 'var(--accent)', borderRadius: '2px',
                animation: 'undo-drain 3s linear forwards',
              }} />
            </div>
          </div>
        );
      })()}

      {/* ── Client profile drawer ── */}
      {drawer && (
        <ClientDrawer
          clientId={drawer.clientId}
          clientName={drawer.clientName}
          partnerId={effectivePartnerId}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
